import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistration } from '@/context/RegistrationContext';
import {
  determineEligibility,
  resolveTrackConflict,
  getCmsEligibleTracks,
} from '@/utils/eligibility';

/**
 * RND-36523 — post-questionnaire finalization (no CMS call).
 *
 * After the eligibility-first reorder, the CMS `$check-eligibility` API has
 * already run up front (MedicareCoverageStep → EligibilityResultStep). Once the
 * filtered health-conditions questionnaire (and, for BH, the PHQ-9/GAD-7
 * screeners) are done, the final track assignment is the intersection of what
 * the patient's conditions support and what CMS confirmed eligible:
 *
 *   finalTracks = determineEligibility(conditions) ∩ getCmsEligibleTracks(results)
 *
 * `determineEligibility` already applies eCKM-priority, the eCKM/CKM mutual
 * exclusion, and — via `isEligibleForBH` → `meetsBHThreshold` — the PRD §6.4
 * sub-threshold BH drop (that predicate stays pure and flow-position independent).
 * The `∩ CMS-eligible` layer then removes any track the conditions support but
 * CMS rejected. No second `$check-eligibility` is fired.
 *
 * Writes the final `eligibility` (which gates TRACK_OFFERING / NOT_ELIGIBLE) and
 * routes: to `/track-offering` when at least one track survives, otherwise to
 * `/not-eligible` (PRD §6.3 "no qualifying condition among eligible tracks").
 */
export const useEligibilityFinalize = () => {
  const { registrationData, updateEligibility } = useRegistration();
  const navigate = useNavigate();

  const finalizeAndRoute = useCallback(() => {
    const conditionEligibility = determineEligibility(
      registrationData.eCKMConditions,
      registrationData.ckmConditions,
      registrationData.behavioralHealthConditions,
      registrationData.bhScreener,
    );
    const cmsEligible = getCmsEligibleTracks(registrationData.eligibilityResults);
    const finalTracks = resolveTrackConflict(
      conditionEligibility.eligibleTracks.filter((track) => cmsEligible.includes(track)),
    );

    updateEligibility({
      eligibleTracks: finalTracks,
      isEligible: finalTracks.length > 0,
      primaryTrack: finalTracks[0] ?? null,
    });

    // Explicit route (not navigateToNextStep) — updateEligibility schedules a
    // re-render, so reading the flow off pre-update context would be stale.
    navigate(finalTracks.length > 0 ? '/track-offering' : '/not-eligible');
  }, [
    registrationData.eCKMConditions,
    registrationData.ckmConditions,
    registrationData.behavioralHealthConditions,
    registrationData.bhScreener,
    registrationData.eligibilityResults,
    updateEligibility,
    navigate,
  ]);

  return { finalizeAndRoute };
};
